import React, { useState } from "react";
import { C, inp, lbl, btnS } from "../../../../lib/styles.js";
import { fl } from "../../../../lib/utils.js";
import { DELIVERY_METHODS, EQUIP_CHIP_COLORS, EQUIP_CHIP_DEFAULT } from "../../../../lib/constants.js";
import { subjectDisplay } from "./CaptureLog.jsx";

function getEquipColor(name) {
  return EQUIP_CHIP_COLORS[name] || EQUIP_CHIP_DEFAULT;
}

export default function DeliveryTab({ job, updateJobFields, editing, isCrew, canSeeMoney }) {
  const [hov, setHov] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [customMethod, setCustomMethod] = useState("");

  const captureLog = job.captureLog || [];
  const deliveryRows = captureLog.filter((r) => r.hiResRequested);
  const upd = (field, val) => updateJobFields({ [field]: val });
  const updateLog = (id, field, val) => upd("captureLog", captureLog.map((r) => r.id === id ? { ...r, [field]: val } : r));

  const methods = job.deliveryMethods?.length ? job.deliveryMethods : (job.deliveryMethod ? [job.deliveryMethod] : []);
  const availableMethods = DELIVERY_METHODS.filter((m) => !methods.includes(m));
  const addMethod = (m) => {
    const clean = (m || "").trim();
    if (clean && !methods.includes(clean)) upd("deliveryMethods", [...methods, clean]);
  };
  const removeMethod = (m) => upd("deliveryMethods", methods.filter((x) => x !== m));
  const deliveredCount = deliveryRows.filter((r) => r.delivered).length;

  const jobFields = (
    <div style={{ marginBottom: 16 }}>
      <div className="jc-jobfields" style={{ display: "grid", gridTemplateColumns: canSeeMoney ? "1fr 1fr 1fr" : "1fr 1fr", gap: 10 }}>
        <div style={{ gridColumn: "1 / -1", marginBottom: 6 }}>
          <label style={lbl}>Delivery Method</label>
          {editing && !isCrew ? (
            <div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
                {methods.map((m) => (
                  <span key={m} style={S.methodChip}>
                    {m}
                    <button onClick={() => removeMethod(m)} style={S.methodRemove}>x</button>
                  </span>
                ))}
                <select aria-label="Add delivery method" value="" onChange={(e) => { if (e.target.value) addMethod(e.target.value); }} style={S.methodSelect}>
                  <option value="">+ Add method...</option>
                  {availableMethods.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <input style={{ ...inp, fontSize: 11, padding: "5px 8px", flex: 1 }} value={customMethod} onChange={(e) => setCustomMethod(e.target.value)} placeholder="Custom method..." onKeyDown={(e) => { if (e.key === "Enter" && customMethod.trim()) { addMethod(customMethod); setCustomMethod(""); } }} />
                {customMethod.trim() && <button onClick={() => { addMethod(customMethod); setCustomMethod(""); }} style={{ ...btnS, padding: "5px 10px", fontSize: 10 }}>Add</button>}
              </div>
            </div>
          ) : (
            <div style={S.val}>{methods.length > 0 ? methods.join(", ") : "-"}</div>
          )}
        </div>
        <div><label style={lbl}>Delivery Due</label>{editing && !isCrew ? <input type="date" style={inp} value={job.deliveryDate || ""} onChange={(e) => upd("deliveryDate", e.target.value)} /> : <div style={S.val}>{job.deliveryDate || "-"}</div>}</div>
        <div><label style={lbl}>Delivered Date</label>{editing && !isCrew ? <input type="date" style={inp} value={job.deliveredDate || ""} onChange={(e) => upd("deliveredDate", e.target.value)} /> : <div style={S.val}>{job.deliveredDate || "-"}</div>}</div>
        {canSeeMoney && <div><label style={lbl}>Modeler / Vendor</label>{editing ? <input style={inp} value={job.modeler || ""} onChange={(e) => upd("modeler", e.target.value)} /> : <div style={S.val}>{job.modeler || "-"}</div>}</div>}
      </div>
    </div>
  );

  if (deliveryRows.length === 0) {
    return (
      <div>
        {jobFields}
        <div style={{ textAlign: "center", padding: 32, color: C.muted, borderTop: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 13, marginBottom: 6 }}>No hi-res items requested yet</div>
          <div style={{ fontSize: 11 }}>Mark items as Hi-Res Requested in the Processing tab to see them here.</div>
        </div>
      </div>
    );
  }

  const gridCols = "30px 46px minmax(140px,1.2fr) minmax(130px,1fr) 0.7fr 58px 58px minmax(150px,1fr) minmax(150px,1fr)";
  const headers = ["#", "Photo", "Subject", "Equipment", "Storage", "HR Done", "Delivered", "Processing Notes", "Delivery Notes"];
  const cellInp = { width: "100%", background: "transparent", border: "none", outline: "none", color: C.text, fontSize: 12, fontFamily: fl, fontWeight: 400, padding: 0 };
  const cellTxt = (v) => ({ cursor: editing ? "text" : "default", display: "block", minHeight: 16, fontSize: 12, color: v ? C.text : `${C.muted}55`, fontFamily: fl, fontWeight: 400 });
  const checkboxStyle = { accentColor: C.green, cursor: editing ? "pointer" : "default", width: 14, height: 14 };

  return (
    <div>
      {jobFields}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <label style={{ ...lbl, marginBottom: 0 }}>Delivery Items</label>
        <span style={{ fontSize: 10, color: deliveredCount === deliveryRows.length ? C.green : C.muted }}>{deliveredCount} of {deliveryRows.length} delivered</span>
      </div>

      <div className="jc-cardtable" style={{ border: `1px solid ${C.border}`, borderRadius: 4, overflowX: "auto", overflowY: "auto", maxHeight: 500 }}>
        <div className="jc-cardtable-head" style={{ display: "grid", gridTemplateColumns: gridCols, background: C.surfaceAlt, borderBottom: `1px solid ${C.border}`, minWidth: "fit-content", position: "sticky", top: 0, zIndex: 1 }}>
          {headers.map((h, i) => <div key={h} style={{ ...S.header, color: h === "Equipment" ? C.blue : C.muted, borderRight: i < headers.length - 1 ? `1px solid ${C.border}` : "none", textAlign: h === "HR Done" || h === "Delivered" ? "center" : "left" }}>{h}</div>)}
        </div>

        {deliveryRows.map((row, idx) => {
          const photo = row.images?.[0] || null;
          const equipNames = row.equipment ? Object.keys(row.equipment).filter((k) => row.equipment[k]) : [];
          const isEditNotes = editingCell?.rid === row.id && editingCell?.f === "deliveryNotes";
          return (
            <div className="jc-cardtable-row" key={row.id} onMouseEnter={() => setHov(row.id)} onMouseLeave={() => setHov(null)} style={{ display: "grid", gridTemplateColumns: gridCols, borderBottom: idx < deliveryRows.length - 1 ? `1px solid ${C.border}` : "none", background: hov === row.id ? C.surfaceAlt : "transparent", alignItems: "center", minWidth: "fit-content" }}>
              <div style={S.rowNum} data-label="#">{idx + 1}</div>
              <div style={S.photoCell} data-label="Photo">{photo?.src ? <img src={photo.src} alt="" style={S.thumb} /> : <div style={S.emptyThumb}>-</div>}</div>
              <div style={S.textCell} data-label="Subject">{subjectDisplay(row) || "-"}</div>
              <div style={S.equipCell} data-label="Equipment">
                {equipNames.map((name) => {
                  const color = getEquipColor(name);
                  return <span key={name} style={{ ...S.equipChip, background: color.bg, border: `1px solid ${color.border}`, color: color.text }}>{name}</span>;
                })}
                {equipNames.length === 0 && <span style={S.emptyText}>-</span>}
              </div>
              <div style={S.textCell} data-label="Storage">{row.storage || "-"}</div>
              <CheckCell label="HR Done" checked={!!row.hiResProcessed} editing={false} style={checkboxStyle} />
              <CheckCell label="Delivered" checked={!!row.delivered} editing={editing} style={checkboxStyle} onChange={() => updateLog(row.id, "delivered", !row.delivered)} testId={`delivery-delivered-${row.id}`} />
              <div style={S.textCell} data-label="Processing Notes">{row.processingNotes || "-"}</div>
              <div style={{ padding: "9px 8px" }} data-label="Delivery Notes">
                {editing && isEditNotes ? (
                  <input autoFocus aria-label="Delivery Notes" value={row.deliveryNotes || ""} onChange={(e) => updateLog(row.id, "deliveryNotes", e.target.value)} onBlur={() => setEditingCell(null)} onKeyDown={(e) => { if (e.key === "Enter") setEditingCell(null); }} style={cellInp} />
                ) : (
                  <span onClick={editing ? () => setEditingCell({ rid: row.id, f: "deliveryNotes" }) : undefined} style={cellTxt(row.deliveryNotes)}>{row.deliveryNotes || "-"}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: C.muted, textAlign: "right", fontFamily: "monospace" }}>
        {deliveryRows.length} item{deliveryRows.length === 1 ? "" : "s"}
      </div>
    </div>
  );
}

function CheckCell({ checked, editing, style, onChange, testId, label }) {
  return (
    <div style={S.checkCell} data-label={label}>
      <input data-testid={testId} aria-label={label} type="checkbox" checked={checked} onChange={editing ? onChange : undefined} disabled={!editing} style={{ ...style, opacity: editing ? 1 : 0.6 }} />
    </div>
  );
}

const S = {
  val: { fontSize: 12, color: C.text, padding: "8px 0", fontFamily: fl },
  methodChip: { display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 3, fontSize: 11, background: `${C.blue}15`, border: `1px solid ${C.blue}30`, color: C.blue },
  methodRemove: { background: "transparent", border: "none", color: "inherit", cursor: "pointer", opacity: 0.6, padding: 0, marginLeft: 2, fontSize: 12, lineHeight: 1 },
  methodSelect: { background: "transparent", border: `1px dashed ${C.border}`, borderRadius: 3, color: C.muted, fontSize: 10, padding: "4px 8px", fontFamily: fl, cursor: "pointer", outline: "none" },
  header: { padding: "8px 6px", fontSize: 9, fontWeight: 400, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: fl, whiteSpace: "nowrap", overflow: "hidden" },
  rowNum: { padding: "9px 6px", fontSize: 11, color: `${C.muted}66`, borderRight: `1px solid ${C.border}`, fontFamily: "monospace" },
  photoCell: { padding: "4px", borderRight: `1px solid ${C.border}` },
  thumb: { width: 36, height: 36, borderRadius: 3, objectFit: "cover", display: "block", border: `1px solid ${C.border}` },
  emptyThumb: { width: 36, height: 36, borderRadius: 3, border: `1px solid ${C.border}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: `${C.muted}44` },
  textCell: { padding: "9px 8px", borderRight: `1px solid ${C.border}`, fontSize: 12, color: C.text, fontFamily: fl, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 },
  equipCell: { display: "flex", flexWrap: "wrap", gap: 4, padding: "6px", minHeight: 40, alignItems: "center", borderRight: `1px solid ${C.border}` },
  equipChip: { display: "inline-flex", alignItems: "center", padding: "3px 8px", borderRadius: 3, fontSize: 10, whiteSpace: "nowrap", fontWeight: 500 },
  emptyText: { fontSize: 10, color: `${C.muted}55` },
  checkCell: { padding: "9px 4px", borderRight: `1px solid ${C.border}`, textAlign: "center" },
};
